import { useEffect, useRef, useState } from "react";
import type { CoachHistory } from "@care-by-ella/contracts";
import { shortDate } from "@/lib/checkin-list";
import { UnauthorizedError } from "@/lib/clinician-api";
import { historyLines } from "@/lib/coach-history";
import { fetchCoachHistory } from "@/lib/coach-now-api";

// TES-1207 — Historik, a sub-level of Patientbild. A-01: "Historiken är för
// fördjupning, inte startsida", so it starts folded and opens on a click or
// on `#historik`.
//
// It replaces the dashed "Tidigare kontakt" placeholder (TES-818). Every row
// comes from GET …/coach-history, which reads only what a coach may see (see
// the contract): no check-in answers, no handoff text, no medical case.

const PAGE = 20;

export function CoachHistoryPanel({
  patientId,
  open,
  onToggle,
  onUnauthorized,
}: {
  patientId: string;
  // Controlled, so a link to `#historik` can open it.
  open: boolean;
  onToggle: (open: boolean) => void;
  onUnauthorized?: () => void;
}) {
  const [history, setHistory] = useState<CoachHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shown, setShown] = useState(PAGE);
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;

  useEffect(() => {
    let cancelled = false;
    setError(null);
    fetchCoachHistory(patientId)
      .then((h) => {
        if (!cancelled) setHistory(h);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof UnauthorizedError) {
          onUnauthorizedRef.current?.();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const lines = history ? historyLines(history.rows) : [];

  return (
    <details
      id="historik"
      open={open}
      onToggle={(e) => onToggle((e.currentTarget as HTMLDetailsElement).open)}
      className="rounded-md border border-surfaceMuted bg-surface p-md"
    >
      <summary className="cursor-pointer font-display text-lg">
        Historik
        {history ? (
          <span className="ml-sm text-sm text-textSecondary">
            ({lines.length})
          </span>
        ) : null}
      </summary>
      <p className="mt-xs max-w-2xl text-sm text-textSecondary">
        Tidigare coachkontakter, återkopplingar, överenskommelser och
        uppföljningar, senaste först.
      </p>
      {error ? (
        <p className="mt-sm text-sm text-danger">Kunde inte hämta: {error}</p>
      ) : !history ? (
        <p className="mt-sm text-sm text-textSecondary">Hämtar…</p>
      ) : lines.length === 0 ? (
        <p className="mt-sm text-sm text-textSecondary">
          Ingen tidigare coachkontakt än.
        </p>
      ) : (
        <>
          <ol className="mt-sm flex flex-col divide-y divide-surfaceMuted">
            {lines.slice(0, shown).map((l) => (
              <li key={l.id} className="py-sm flex flex-col gap-xs">
                <div className="flex flex-wrap items-baseline gap-x-sm text-sm">
                  <span className="font-medium">{l.title}</span>
                  {l.outcome ? (
                    <span className="text-textSecondary">· {l.outcome}</span>
                  ) : null}
                </div>
                <p className="text-xs text-textSecondary">
                  {shortDate(l.at)}
                  {l.by ? ` · ${l.by}` : ""}
                </p>
                {l.text ? (
                  <p className="max-w-2xl text-sm text-textPrimary whitespace-pre-line">
                    {l.textLabel ? (
                      <span className="text-textSecondary">
                        {l.textLabel}:{" "}
                      </span>
                    ) : null}
                    {l.text}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
          {lines.length > shown ? (
            <button
              type="button"
              onClick={() => setShown((n) => n + PAGE)}
              className="mt-sm px-md py-sm rounded-md border border-surfaceMuted text-sm"
            >
              Visa fler ({lines.length - shown} till)
            </button>
          ) : null}
        </>
      )}
    </details>
  );
}
